import { css, cx } from '@linaria/atomic';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton } from '../../naked';
import { IconButton } from '../icon-button';
import { useSlideCarousel } from './provider';

type SlideNavigationProps = {
  variant?: 'default' | 'fullIndicators' | 'centeredIndicators';
  activeCenteredIndicatorColor?: string;
  trackingId?: string;
};

export const SlideNavigation = ({
  variant = 'default',
  activeCenteredIndicatorColor,
  trackingId = 'navigate-slide',
}: SlideNavigationProps) => {
  const { activeId, setActiveId, slideIds } = useSlideCarousel();
  const activeIndex = slideIds.indexOf(activeId);

  const handlePrev = () => {
    const prev = slideIds[activeIndex - 1];
    if (activeIndex > 0 && prev) {
      setActiveId(prev);
    }
  };

  const handleNext = () => {
    const next = slideIds[activeIndex + 1];
    if (activeIndex < slideIds.length - 1 && next) {
      setActiveId(next);
    }
  };

  const isFullIndicatorsVariant = variant === 'fullIndicators';
  const isCenteredIndicators = variant === 'centeredIndicators';

  return (
    <footer className={buttonContainerStyle}>
      {!isFullIndicatorsVariant && !isCenteredIndicators && (
        <div className={navRowStyle}>
          <IconButton
            className={buttonStyle}
            size='xsmall'
            label='Next'
            disabled={activeIndex === 0}
            onClick={handlePrev}
            trackingId={`${trackingId}-prev`}
          >
            <Icon name='caret-left' />
          </IconButton>
          <IconButton
            className={buttonStyle}
            size='xsmall'
            label='Forward'
            disabled={activeIndex === slideIds.length - 1}
            onClick={handleNext}
            trackingId={`${trackingId}-next`}
          >
            <Icon name='caret-right' />
          </IconButton>
        </div>
      )}
      <div className={cx(navRowStyle, isFullIndicatorsVariant && indicatorRowFullStyle)}>
        {slideIds.map((id, index) => (
          <ActiveSectionIndicator
            variant={variant}
            key={id}
            isActive={activeIndex === index}
            onClick={() => setActiveId(id)}
            activeCenteredIndicatorColor={activeCenteredIndicatorColor}
          />
        ))}
      </div>
    </footer>
  );
};

const navRowStyle = css`
  display: flex;
  gap: ${theme.spacing(1)};
`;

const indicatorRowFullStyle = css`
  width: 100%;
`;

const buttonStyle = css`
  padding: ${theme.spacing(0.5)};
`;

interface ActiveSectionIndicatorProps {
  isActive: boolean;
  onClick: () => void;
  variant?: SlideNavigationProps['variant'];
  activeCenteredIndicatorColor?: string;
}

const indicatorBaseStyle = css`
  display: block;
  height: 8px;
  width: 8px;
  border-radius: ${theme.borderRadius.full};
  transition: background-color 0.3s ease;
  cursor: pointer;
`;

const indicatorActiveStyle = css`
  background-color: ${theme.colors.neutral90};
`;

const indicatorInactiveStyle = css`
  background-color: ${theme.colors.neutral20};
`;

const indicatorFullStyle = css`
  width: 100%;
`;

const indicatorFullActiveStyle = css`
  background-color: ${theme.colors.primary50};
`;

const indicatorFullInactiveStyle = css`
  background-color: ${theme.colors.neutral10};
`;

const indicatorCenteredInactiveStyle = css`
  background-color: ${theme.colors.white};
`;

const ActiveSectionIndicator = ({
  isActive,
  onClick,
  variant,
  activeCenteredIndicatorColor,
}: ActiveSectionIndicatorProps) => {
  const isFullIndicatorsVariant = variant === 'fullIndicators';
  const isCenteredIndicators = variant === 'centeredIndicators';

  return (
    <NakedButton
      onClick={onClick}
      className={cx(
        indicatorBaseStyle,
        !isFullIndicatorsVariant && !isCenteredIndicators && (isActive ? indicatorActiveStyle : indicatorInactiveStyle),
        isFullIndicatorsVariant && indicatorFullStyle,
        isFullIndicatorsVariant && (isActive ? indicatorFullActiveStyle : indicatorFullInactiveStyle),
        isCenteredIndicators && !isActive && indicatorCenteredInactiveStyle
      )}
      style={
        isCenteredIndicators && isActive && activeCenteredIndicatorColor
          ? { backgroundColor: activeCenteredIndicatorColor }
          : undefined
      }
    />
  );
};

const buttonContainerStyle = css`
  display: flex;
  justify-content: space-between;
  gap: ${theme.spacing(1)};
  align-items: baseline;
`;
